const DEFAULT_CURRENCY_SYMBOL = '\u00A3';

const CURRENCY_DISPLAY_SYMBOLS = {
    GBP: '\u00A3',
    USD: '$',
    EUR: '\u20AC',
    INR: '\u20B9',
    AED: 'AED ',
    AD: 'AED ',
    DH: 'AED ',
    DHS: 'AED ',
};

export const toAmountNumber = (value) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }

    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }

    if (typeof value === 'string') {
        const normalizedValue = value.replace(/,/g, '').trim();
        if (!normalizedValue) return 0;

        const numericValue = Number(normalizedValue);
        if (Number.isFinite(numericValue)) return numericValue;

        const parsedValue = parseFloat(normalizedValue.replace(/[^\d.-]/g, ''));
        return Number.isFinite(parsedValue) ? parsedValue : 0;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
};

export const resolveCurrencyDisplay = (currencyCode, currencySymbol) => {
    const code = String(currencyCode || '').trim().toUpperCase();
    const symbol = String(currencySymbol || '').trim();
    if (CURRENCY_DISPLAY_SYMBOLS[code]) {
        return CURRENCY_DISPLAY_SYMBOLS[code];
    }
    if (symbol && symbol !== DEFAULT_CURRENCY_SYMBOL && symbol !== 'Â£') {
        return symbol.length > 1 && !symbol.endsWith(' ') ? `${symbol} ` : symbol;
    }
    if (code && code !== 'GBP') {
        return `${code} `;
    }
    return symbol || DEFAULT_CURRENCY_SYMBOL;
};

export const formatCurrencyAmount = (value, currencySymbol = DEFAULT_CURRENCY_SYMBOL, currencyCode = '') => {
    const displaySymbol = resolveCurrencyDisplay(currencyCode, currencySymbol);
    return `${displaySymbol}${toAmountNumber(value).toFixed(2)}`;
};

export const normalizeTaxItem = (tax = {}, index = 0) => {
    const fallbackId = tax?.taxName ?? tax?.tax_name ?? tax?.name ?? tax?.label ?? `tax-${index}`;
    return {
        taxId: tax?.taxId ?? tax?.tax_id ?? tax?.id ?? tax?.value ?? fallbackId,
        taxName: tax?.taxName ?? tax?.tax_name ?? tax?.name ?? tax?.label ?? 'Tax',
        taxRate: toAmountNumber(tax?.taxRate ?? tax?.tax_rate ?? tax?.rate ?? tax?.percentage ?? tax?.percent ?? 0),
        taxAmount: toAmountNumber(tax?.taxAmount ?? tax?.tax_amount ?? tax?.amount ?? tax?.value_amount ?? tax?.calculated_amount ?? 0),
    };
};

export const normalizeTaxes = (taxes) => {
    if (!Array.isArray(taxes)) return [];
    return taxes.map((tax, index) => normalizeTaxItem(tax, index));
};

export const getTaxNamesLabel = (taxNames, taxes = []) => {
    if (typeof taxNames === 'string' && taxNames.trim()) return taxNames;
    const normalizedTaxes = normalizeTaxes(taxes);
    if (!normalizedTaxes.length) return 'No tax';
    return normalizedTaxes.map((tax) => tax.taxName).join(', ');
};

export const getTotalTaxRate = ({ taxes = [], totalTaxRate = null }) => {
    const normalizedTaxes = normalizeTaxes(taxes);
    const derivedTaxRate = normalizedTaxes.reduce((sum, tax) => sum + toAmountNumber(tax.taxRate), 0);

    if (totalTaxRate !== null && totalTaxRate !== undefined && totalTaxRate !== '') {
        return toAmountNumber(totalTaxRate);
    }

    return derivedTaxRate;
};

export const buildTaxSummary = ({
    taxes = [],
    totalTaxRate = null,
    taxNames = '',
    baseAmount = 0,
    taxAmount = null,
    totalAmount = null,
}) => {
    const normalizedTaxes = normalizeTaxes(taxes);
    const resolvedTotalTaxRate = getTotalTaxRate({ taxes: normalizedTaxes, totalTaxRate });
    const resolvedBaseAmount = toAmountNumber(baseAmount);

    let resolvedTaxAmount = taxAmount;
    const missingTaxAmount =
        resolvedTaxAmount === null ||
        resolvedTaxAmount === undefined ||
        resolvedTaxAmount === '';
    const shouldInferTaxAmount =
        missingTaxAmount &&
        (resolvedTotalTaxRate > 0 || normalizedTaxes.some((tax) => tax.taxAmount > 0));

    if (shouldInferTaxAmount) {
        if (normalizedTaxes.some((tax) => tax.taxAmount > 0)) {
            resolvedTaxAmount = normalizedTaxes.reduce((sum, tax) => sum + toAmountNumber(tax.taxAmount), 0);
        } else {
            resolvedTaxAmount = (resolvedBaseAmount * resolvedTotalTaxRate) / 100;
        }
    }

    const providedTotalAmount = toAmountNumber(totalAmount);
    const shouldInferTotalAmount =
        totalAmount === null ||
        totalAmount === undefined ||
        totalAmount === '' ||
        (providedTotalAmount <= resolvedBaseAmount && toAmountNumber(resolvedTaxAmount) > 0);

    const resolvedTotalAmount = shouldInferTotalAmount
        ? resolvedBaseAmount + toAmountNumber(resolvedTaxAmount)
        : providedTotalAmount;

    const breakdown = normalizedTaxes.map((tax) => ({
        ...tax,
        taxAmount: resolvedTotalTaxRate > 0 && tax.taxAmount > 0
            ? tax.taxAmount
            : resolvedTotalTaxRate > 0
                ? (resolvedBaseAmount * tax.taxRate) / 100
                : 0,
    }));

    return {
        taxes: breakdown,
        taxNamesLabel: getTaxNamesLabel(taxNames, normalizedTaxes),
        totalTaxRate: resolvedTotalTaxRate,
        baseAmount: resolvedBaseAmount,
        taxAmount: toAmountNumber(resolvedTaxAmount),
        totalAmount: resolvedTotalAmount,
        hasTax: resolvedTotalTaxRate > 0 || breakdown.length > 0 || toAmountNumber(resolvedTaxAmount) > 0,
    };
};

export const extractTaxSnapshot = (source = {}) => {
    return buildTaxSummary({
        taxes: source?.tax_details ?? source?.taxes ?? source?.tax_breakdown ?? source?.taxBreakdown ?? source?.applied_taxes ?? [],
        totalTaxRate: source?.total_tax_rate ?? source?.totalTaxRate ?? source?.tax_rate ?? source?.taxRate ?? source?.tax_percentage ?? source?.taxPercentage ?? 0,
        taxNames: source?.tax_names ?? source?.taxNames ?? source?.tax_label ?? source?.taxLabel ?? '',
        baseAmount: source?.base_amount ?? source?.baseAmount ?? source?.wallet_credit ?? source?.walletCredit ?? source?.sub_total ?? source?.subtotal ?? source?.amount_before_tax ?? 0,
        taxAmount: source?.tax_amount ?? source?.taxAmount ?? source?.applied_tax ?? source?.appliedTax ?? 0,
        totalAmount: source?.total_amount ?? source?.totalAmount ?? source?.amount ?? source?.total_paid ?? source?.totalPaid ?? 0,
    });
};
